/**
 * SecretaryChat — the container-scoped chat modal (Ken 2026-10-22, KEN only).
 *
 * Opened by clicking a pane-1 container NAME (category / Client Activity /
 * matter / Personal). A FLOATING, draggable panel over the workspace — chat
 * bubbles, dismissible (Escape / ×). It does NOT dim or block the workspace, so
 * clicking a cited artifact ID can select that item in panes 2 & 3 while the
 * panel stays open; drag the title bar to move the panel out of the way.
 *
 * Decoupled opener: pane-1 rows call openSecretaryChat(scope, title); a single
 * <SecretaryChatHost/> (mounted once in the sidebar) listens and renders it.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { secretaryChat, type SecretaryScope, type SecretarySource } from '../api'
import { useAuth } from '../auth'

type Msg = { role: 'user' | 'assistant'; content: string }

const EVT = 'me-open-secretary'
const PANEL_W = 616

export function openSecretaryChat(scope: SecretaryScope, title: string) {
  window.dispatchEvent(new CustomEvent(EVT, { detail: { scope, title } }))
}

/** Mount once. Listens for openSecretaryChat() and renders the panel. */
export function SecretaryChatHost() {
  const [open, setOpen] = useState<{ scope: SecretaryScope; title: string } | null>(null)
  useEffect(() => {
    const h = (e: Event) => setOpen((e as CustomEvent).detail)
    window.addEventListener(EVT, h)
    return () => window.removeEventListener(EVT, h)
  }, [])
  if (!open) return null
  return (
    <SecretaryChat
      key={`${open.scope.type}:${open.scope.id ?? 'unfiled'}`}
      scope={open.scope}
      title={open.title}
      onClose={() => setOpen(null)}
    />
  )
}

// Hyphen variants the model may emit inside a code (figure dash, non-breaking
// hyphen, en/em dash, minus) — matched without rewriting the displayed prose.
const HYPHENS = /[‐‑‒–—−]/g
const HYPHEN_CLASS = '[\\u2010\\u2011\\u2012\\u2013\\u2014\\u2212-]'
function esc(s: string) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') }

// A cited-artifact link, rendered as a TWO-PART citation (Ken 2026-10-25):
// light-grey parentheses around the evidence name, which carries a 1px BOTTOM
// RULE (border-bottom, not text-decoration, so the rule clears descenders).
// Hover darkens the name toward body ink and darkens the rule; the parentheses
// carry the lightening and never go blue. The name never lightens past #6E6557
// (4.7:1 on the manila ground — it's the evidence).
function CitationLink({ paren, name, chip, onActivate, title }: {
  paren: boolean
  name: ReactNode
  chip: boolean
  onActivate: () => void
  title: string
}) {
  const [hov, setHov] = useState(false)
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate() } }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onFocus={() => setHov(true)}
      onBlur={() => setHov(false)}
      style={{ cursor: 'pointer' }}
      title={title}
    >
      {paren && <span style={{ color: '#A79C8C' }}>(</span>}
      <span
        style={chip
          ? { color: hov ? '#1C1910' : '#6E6557', background: 'var(--me-accent-bg)', borderRadius: 3, padding: '0 3px' }
          : { color: hov ? '#1C1910' : '#6E6557', borderBottom: `1px solid ${hov ? '#6E6557' : '#C3B9A6'}` }}
      >{name}</span>
      {paren && <span style={{ color: '#A79C8C' }}>)</span>}
    </span>
  )
}

function SecretaryChat({ scope, title, onClose }: {
  scope: SecretaryScope
  title: string
  onClose: () => void
}) {
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  // Per-user citation settings (KEN). content = which form; style = how drawn.
  const authCtx = useAuth()
  const citeContent = (authCtx.status === 'authenticated' ? authCtx.user.citation_content : 'first') || 'first'
  const citeStyle = (authCtx.status === 'authenticated' ? authCtx.user.citation_style : 'underline') || 'underline'
  // Accumulated artifact citations in scope: short code → ids (for linkifying).
  const sourcesRef = useRef<Map<string, SecretarySource>>(new Map())

  // Floating position (clamped to the viewport). null → centred-near-top default.
  const [pos, setPos] = useState<{ x: number; y: number }>(() => ({
    x: Math.max(8, Math.round((window.innerWidth - PANEL_W) / 2)),
    y: Math.round(window.innerHeight * 0.06),
  }))
  const drag = useRef<{ dx: number; dy: number } | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9 }) }, [messages, busy])

  function onHeaderPointerDown(e: React.PointerEvent) {
    // Don't start a drag from the close button.
    if ((e.target as HTMLElement).closest('button')) return
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y }
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  function onHeaderPointerMove(e: React.PointerEvent) {
    if (!drag.current) return
    const x = e.clientX - drag.current.dx
    const y = e.clientY - drag.current.dy
    setPos({
      x: Math.min(Math.max(8 - PANEL_W + 120, x), window.innerWidth - 120),
      y: Math.min(Math.max(0, y), window.innerHeight - 48),
    })
  }
  function endDrag() { drag.current = null }

  async function send() {
    const text = input.trim()
    if (!text || busy) return
    const next: Msg[] = [...messages, { role: 'user', content: text }]
    setMessages(next)
    setInput('')
    setBusy(true)
    try {
      const r = await secretaryChat(scope, next)
      for (const s of r.sources || []) sourcesRef.current.set(s.code, s)
      setMessages([...next, { role: 'assistant', content: r.reply || '(no reply)' }])
    } catch {
      setMessages([...next, { role: 'assistant', content: 'The Secretary is unavailable right now.' }])
    } finally {
      setBusy(false)
    }
  }

  // Clicking a citation selects the artifact in panes 2 & 3 WITHOUT closing the
  // panel — a query-param navigation to the same /workspace route (the panel is a
  // portal mounted in the sidebar, so it stays put).
  function selectArtifact(code: string) {
    const s = sourcesRef.current.get(code)
    if (!s) return
    navigate(`/workspace?matter=${s.matter_id}&artifact=${s.artifact_id}`)
  }

  // Turn cited short codes (EMAL-001374, ATCH-001367…) into two-part citation
  // links, preserving the surrounding prose exactly (incl. its em-dashes). When
  // the code sits inside parentheses — "(EMAL-001374)", the usual case — the
  // WHOLE "(…)" becomes the citation (light parens + the evidence name).
  function renderAssistant(textContent: string): ReactNode {
    const codes = Array.from(sourcesRef.current.keys())
    if (!codes.length) return textContent
    const re = new RegExp(
      '(' + codes.map((c) => c.split('-').map(esc).join(HYPHEN_CLASS)).join('|') + ')', 'g')
    const out: ReactNode[] = []
    let last = 0
    let m: RegExpExecArray | null
    while ((m = re.exec(textContent)) !== null) {
      const codeStart = m.index
      const codeEnd = m.index + m[0].length
      // Consume the surrounding parentheses when present, so the citation is the
      // whole "(name)" rather than just the inner code.
      const paren = textContent[codeStart - 1] === '(' && textContent[codeEnd] === ')'
      const segStart = paren ? codeStart - 1 : codeStart
      const segEnd = paren ? codeEnd + 1 : codeEnd
      if (segStart > last) out.push(textContent.slice(last, segStart))
      const shown = m[0]
      const key = shown.replace(HYPHENS, '-')
      const s = sourcesRef.current.get(key)
      // CONTENT form (user setting): 'code' = raw code; 'first' = sender first name
      // only (the live default); 'first_subject' = (first name, *subject*). Falls
      // back to the code when a source has no sender/subject (e.g. attachments).
      let name: ReactNode = shown
      if (citeContent === 'first') {
        name = s?.sender || shown
      } else if (citeContent === 'first_subject') {
        name = (s && (s.sender || s.subject))
          ? <>{s.sender}{s.sender && s.subject ? ', ' : ''}{s.subject ? <em>{s.subject}</em> : null}</>
          : shown
      }
      out.push(
        <CitationLink
          key={`${m.index}`}
          paren={paren}
          chip={citeStyle === 'chip'}
          name={name}
          onActivate={() => selectArtifact(key)}
          title={s?.date || 'Open in the panes'}
        />,
      )
      last = segEnd
    }
    if (last < textContent.length) out.push(textContent.slice(last))
    return out
  }

  return createPortal(
    <>
      {/* 16% ink scrim — lifts the panel with neutral depth (not colour). Click to dismiss. */}
      <div
        className="fixed inset-0 z-[1000]"
        style={{ background: 'rgba(28, 26, 23, 0.16)' }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="me-card fixed z-[1001] flex max-h-[70vh] flex-col overflow-hidden rounded-lg shadow-2xl"
        style={{ left: pos.x, top: pos.y, width: PANEL_W, background: '#FFFFFF' }}
      >
      <div
        className="flex shrink-0 cursor-move select-none items-center justify-between border-b me-border px-3 py-2"
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        title="Drag to move"
      >
        <div className="me-ai-header truncate text-[13px] font-medium me-text">Ask me about <span className="me-ai-title">{title}</span></div>
        <button onClick={onClose} className="me-btn" aria-label="Close"><X size={13} /></button>
      </div>
      <div ref={scrollRef} className="me-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-2.5">
        {/* Empty state intentionally blank (Ken 2026-10-28): the matter/object name
            is already in the header, so the middle area shows nothing until there
            are messages — no repeated name. */}
        {messages.map((m, i) => {
          const isUser = m.role === 'user'
          return (
            <div key={i} className={isUser ? 'max-w-[85%] self-end' : 'max-w-[92%] self-start'}>
              <div
                className={`whitespace-pre-wrap rounded-lg px-3 py-1.5 text-[12px] leading-snug me-text ${isUser ? 'me-chat-question border me-border' : 'me-chat-answer'}`}
                style={{ background: isUser ? '#FFFFFF' : '#F3E7D0' }}
              >
                {isUser ? m.content : renderAssistant(m.content)}
              </div>
            </div>
          )
        })}
        {busy && (
          <div className="self-start rounded-lg px-3 py-1.5 text-[12px] me-text-mute"
               style={{ background: '#F3E7D0' }}>…</div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2 border-t me-border px-3 py-2">
        <input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send() }}
          placeholder="What is going on here?"
          className="me-input me-search-ken flex-1 text-[12px]"
          disabled={busy}
        />
        <button onClick={send} disabled={busy || !input.trim()} className="me-btn me-btn-outline text-[12px]">
          Ask
        </button>
      </div>
      </div>
    </>,
    document.body,
  )
}
